/**
 * @file     ContentBlocks.tsx
 * @purpose  Dispatches a content-engine ContentBlock[] to its React renderers in order, and owns the heading anchor ids the article table of contents scrolls to.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAppTheme } from '@/core/theme/useAppTheme';
import type { ContentBlock } from '@/services/content-engine/types';
import { ProseBlock } from './ProseBlock';
import { StatGridBlock } from './StatGridBlock';
import { ListingCardsBlock } from './ListingCardsBlock';
import { TypeTableBlock } from './TypeTableBlock';
import { PriceBandsBlock } from './PriceBandsBlock';
import { RoundupBlock } from './RoundupBlock';
import { InternalLinksBlock } from './InternalLinksBlock';
import { FaqBlock } from './FaqBlock';
import { CtaBlock } from './CtaBlock';
import { DisclosureBlock } from './DisclosureBlock';
import { NewsReadBlock } from './NewsReadBlock';

/** Stable anchor id for a heading block. Shared with the table of contents so
 *  both sides derive the same target without storing ids on the post. */
export const headingAnchorId = (text: string, index: number) =>
  `s-${index}-${text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)}`;

export type HeadingEntry = { id: string; text: string; level: 2 | 3 };

export const collectHeadings = (blocks: ContentBlock[]): HeadingEntry[] =>
  blocks.flatMap((block, index) =>
    block.t === 'heading' ? [{ id: headingAnchorId(block.text, index), text: block.text, level: block.level }] : [],
  );

type ContentBlocksProps = { blocks: ContentBlock[] };

export const ContentBlocks = ({ blocks }: ContentBlocksProps) => {
  const { theme } = useAppTheme();

  if (blocks.length === 0) return null;

  return (
    // minmax(0, 1fr), not the implicit 1fr: a grid item defaults to
    // min-width:auto, so any block wider than the viewport (the table's 520px
    // scroll wrapper) stretched the track instead of scrolling inside it, and
    // the whole article scrolled sideways on a 390px phone.
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: { xs: '28px', md: '40px' },
      }}
    >
      {blocks.map((block, index) => {
        const key = `${block.t}-${index}`;

        switch (block.t) {
          case 'prose':
            return <ProseBlock key={key} html={block.html} />;

          case 'heading':
            return (
              <Typography
                key={key}
                id={headingAnchorId(block.text, index)}
                component={block.level === 2 ? 'h2' : 'h3'}
                sx={{
                  scrollMarginTop: '24px',
                  fontFamily: theme.typography.fontFamilyHeading,
                  fontSize: block.level === 2 ? { xs: 22, md: 26 } : { xs: 19, md: 20 },
                  fontWeight: block.level === 2 ? 600 : 500,
                  letterSpacing: '-0.015em',
                  lineHeight: 1.2,
                  color: theme.semanticColors.textPrimary,
                  mb: '-12px',
                }}
              >
                {block.text}
              </Typography>
            );

          case 'stat_grid':
            return <StatGridBlock key={key} {...block} />;
          case 'listing_cards':
            return <ListingCardsBlock key={key} {...block} />;
          case 'type_table':
            return <TypeTableBlock key={key} {...block} />;
          case 'price_bands':
            return <PriceBandsBlock key={key} {...block} />;
          case 'local_roundup':
          case 'news_digest':
            return <RoundupBlock key={key} {...block} />;
          case 'internal_links':
            return <InternalLinksBlock key={key} {...block} />;
          case 'faq':
            return <FaqBlock key={key} {...block} />;
          case 'cta_search':
          case 'cta_valuation':
            return <CtaBlock key={key} {...block} />;
          case 'disclosure':
            return <DisclosureBlock key={key} {...block} />;
          case 'news_read':
            return <NewsReadBlock key={key} {...block} />;
          default:
            return null;
        }
      })}
    </Box>
  );
};
